import type { Person, RecurringTaskRow, WorkOrderPriority, WorkOrderRow } from '@routeback/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { TruckPicker } from '../../components/TruckPicker';
import { useApp } from '../../lib/app';
import { CATEGORIES } from '../maintenance/maintenance';
import { todayIn } from '../timeoff/timeoff';
import type { Completion } from './actions';
import { blankWorkOrder } from './actions';
import { everyLabel, guessTask, type RecurringLink } from './recurring';
import { formatMoney, parseMoney, parseOdometer, PRIORITIES } from './workorders';

const err = (s: string) => <span className="small" style={{ color: 'var(--danger)' }}>{s}</span>;

export function PrioritySelect({ id, value, onChange }: { id?: string; value: WorkOrderPriority; onChange: (p: WorkOrderPriority) => void }) {
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value as WorkOrderPriority)}>
      {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
    </select>
  );
}

/** The plugin's "Mark complete" fields, also used by "Log completed work". */
function CompletionFields({ v, set, idp, tasks }: { v: CompletionDraft; set: (p: Partial<CompletionDraft>) => void; idp: string; tasks?: RecurringTaskRow[] }) {
  const odo = parseOdometer(v.odometer);
  const cost = parseMoney(v.cost);
  return (
    <>
      {tasks && <RecurringPicker v={v} set={set} idp={idp} tasks={tasks} />}
      <div className="ar-pair">
        <div className="field"><label htmlFor={`${idp}-shop`}>Shop / performed by *</label><input id={`${idp}-shop`} value={v.vendor} placeholder="e.g. In-house, Pete's Truck Shop" onChange={(e) => set({ vendor: e.target.value })} /></div>
        <div className="field"><label htmlFor={`${idp}-date`}>Completed on *</label><input id={`${idp}-date`} type="date" value={v.completed_on} onChange={(e) => set({ completed_on: e.target.value })} /></div>
      </div>
      <div className="ar-pair">
        <div className="field"><label htmlFor={`${idp}-odo`}>Odometer</label><input id={`${idp}-odo`} inputMode="numeric" value={v.odometer} placeholder="e.g. 142,500" onChange={(e) => set({ odometer: e.target.value })} />{Number.isNaN(odo) && err('Miles as a number, or leave blank')}</div>
        <div className="field"><label htmlFor={`${idp}-cost`}>Cost</label><input id={`${idp}-cost`} inputMode="decimal" value={v.cost} placeholder="e.g. 482.50" onChange={(e) => set({ cost: e.target.value })} />{Number.isNaN(cost) && err('Dollars, e.g. 482.50')}</div>
      </div>
      <div className="field"><label htmlFor={`${idp}-done`}>What was done *</label><textarea id={`${idp}-done`} rows={3} value={v.work_done} placeholder="A short summary — this is what shows on the MMR" onChange={(e) => set({ work_done: e.target.value })} /></div>
      <label className="row mmr-check" style={{ gap: 10, alignItems: 'flex-start' }}>
        <input type="checkbox" checked={v.include_mmr} onChange={(e) => set({ include_mmr: e.target.checked })} style={{ width: 20, height: 20, marginTop: 2, flex: 'none' }} />
        <span><strong>Include on MMR</strong> (monthly carrier report)<br /><span className="muted small">Tick for compliance work — brakes, tires, lights, inspections. Washes and cosmetic work: leave unticked.</span></span>
      </label>
    </>
  );
}

/**
 * "Is this a recurring service?" for a job no rule booked — e.g. a filter
 * changed in-house. Picking a rule makes this job count for it (the next one
 * is booked from this date); "start a new one" makes the rule from here, which
 * is how a service gets on the schedule the first time it's done.
 */
function RecurringPicker({ v, set, idp, tasks }: { v: CompletionDraft; set: (p: Partial<CompletionDraft>) => void; idp: string; tasks: RecurringTaskRow[] }) {
  const days = parseInt(v.newDays, 10);
  return (
    <div className="field rc-link">
      <label htmlFor={`${idp}-rec`}>Recurring service?</label>
      <select id={`${idp}-rec`} value={v.link} onChange={(e) => set({ link: e.target.value })}>
        <option value="none">No — one-off repair</option>
        {tasks.map((t) => <option key={t.id} value={t.id}>{t.name} · {everyLabel(t.interval_days)}</option>)}
        <option value="new">+ Start a new recurring service…</option>
      </select>
      {v.link !== 'none' && v.link !== 'new' && <span className="muted small">Counts as this truck's {tasks.find((t) => t.id === v.link)?.name}: the next one is booked from the completion date (and replaces any one already booked).</span>}
      {v.link === 'new' && (
        <div className="ar-pair" style={{ marginTop: 6 }}>
          <div className="field"><label htmlFor={`${idp}-rn`}>Service name</label><input id={`${idp}-rn`} value={v.newName} maxLength={80} placeholder="e.g. Air filter" onChange={(e) => set({ newName: e.target.value })} /></div>
          <div className="field"><label htmlFor={`${idp}-rd`}>Every (days)</label><input id={`${idp}-rd`} inputMode="numeric" value={v.newDays} onChange={(e) => set({ newDays: e.target.value.replace(/\D/g, '') })} />
            <span className="muted small">{days > 0 ? `${everyLabel(days)} — next one due from the completion date` : '90 = 3 months, 365 = yearly'}</span></div>
        </div>
      )}
    </div>
  );
}

/** Active rules, for the picker (undefined while loading). */
function useRules(): RecurringTaskRow[] | undefined {
  const app = useApp();
  return useLiveQuery(async () => (await app.repos.recurringTasks.listLive()).filter((t) => t.active).sort((a, b) => a.name.localeCompare(b.name)), [app.repos]);
}

interface CompletionDraft {
  vendor: string; completed_on: string; odometer: string; cost: string; work_done: string; include_mmr: boolean;
  link: string; newName: string; newDays: string;
}
function draftFrom(wo: WorkOrderRow | null, today: string): CompletionDraft {
  return {
    vendor: wo?.vendor ?? '', completed_on: wo?.completed_on ?? today, odometer: wo?.odometer != null ? String(wo.odometer) : '',
    cost: wo?.cost_cents != null ? formatMoney(wo.cost_cents).replace(/[$,]/g, '') : '', work_done: wo?.work_done ?? '', include_mmr: wo?.include_mmr ?? false,
    link: 'none', newName: wo?.title ?? '', newDays: '90',
  };
}
function completionOf(d: CompletionDraft, withLink = false): Completion | null {
  const odometer = parseOdometer(d.odometer);
  const cost_cents = parseMoney(d.cost);
  if (!d.vendor.trim() || !d.completed_on || !d.work_done.trim() || Number.isNaN(odometer) || Number.isNaN(cost_cents)) return null;
  let recurring: RecurringLink | undefined;
  if (withLink) {
    const days = parseInt(d.newDays, 10);
    if (d.link === 'new' && (!d.newName.trim() || !(days > 0 && days <= 3650))) return null;
    recurring = d.link === 'none' ? { kind: 'none' } : d.link === 'new' ? { kind: 'new', name: d.newName.trim(), days } : { kind: 'task', taskId: d.link };
  }
  return { vendor: d.vendor.trim(), completed_on: d.completed_on, odometer, cost_cents, work_done: d.work_done.trim(), include_mmr: d.include_mmr, ...(recurring ? { recurring } : {}) };
}

/** Mark a job complete (or fix the completion details of a finished one). */
export function CompleteForm({ wo, onSave, onCancel }: { wo: WorkOrderRow; onSave: (c: Completion) => void; onCancel: () => void }) {
  const app = useApp();
  const rules = useRules();
  // Finishing a job no rule booked: offer to count it as a recurring service (guessing from its title).
  const offer = !wo.recurring_task_id && wo.status !== 'done' && !!wo.truck_id;
  const [d, setD] = useState(() => ({ ...draftFrom(wo, todayIn(app.details.timezone)), work_done: wo.work_done || (wo.recurring_task_id ? wo.title : '') }));
  const [guessed, setGuessed] = useState(false);
  if (offer && rules && !guessed) {
    setGuessed(true);
    const g = guessTask(`${wo.title} ${wo.description}`.trim(), rules) ?? guessTask(wo.title, rules);
    if (g) setD((x) => ({ ...x, link: g.id }));
  }
  const c = completionOf(d, offer);
  return (
    <div>
      <CompletionFields v={d} set={(p) => setD({ ...d, ...p })} idp="cf" tasks={offer ? rules ?? [] : undefined} />
      <div className="row" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={!c} onClick={() => c && onSave(c)}>{wo.status === 'done' ? 'Save' : 'Complete & close'}</button>
      </div>
    </div>
  );
}

/**
 * New work order — or, with `completed`, the plugin's "Log completed work"
 * (a job that's already been done, straight onto the Completed list).
 */
export function NewJobForm({ completed, people, onSave, onCancel }: { completed?: boolean; people: Person[]; onSave: (wo: WorkOrderRow, recurring?: RecurringLink) => void; onCancel: () => void }) {
  const app = useApp();
  const rules = useRules();
  const today = todayIn(app.details.timezone);
  const [truck, setTruck] = useState<string | null>(null);
  const [priority, setPriority] = useState<WorkOrderPriority>('normal');
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [reportedBy, setReportedBy] = useState('');
  const [opened, setOpened] = useState(today);
  const [mech, setMech] = useState(false);
  const [d, setD] = useState(() => draftFrom(null, today));
  const drivers = people.filter((p) => p.role === 'driver' && p.status === 'active');
  const c = completed ? completionOf(d, true) : null;
  const ok = !!truck && !!(title.trim() || desc.trim()) && (!completed || !!c);
  const save = () => {
    if (!ok) return;
    const { recurring, ...fields } = c ?? { recurring: undefined };
    onSave(blankWorkOrder(app.repos, {
      truck_id: truck, priority, title: title.trim(), description: desc.trim(), reported_by: reportedBy || null,
      opened_on: completed ? c!.completed_on : opened, for_mechanic: !completed && mech,
      ...(completed ? { ...fields, status: 'done' as const, completed_by: app.userId } : {}),
    }), recurring);
  };
  const setTitleGuess = (t: string) => {
    setTitle(t);
    if (completed && rules && (d.link === 'none' || rules.some((r) => r.id === d.link))) {
      const g = guessTask(t, rules);
      setD((x) => ({ ...x, link: g ? g.id : x.link === 'new' ? 'new' : 'none', newName: x.link === 'new' ? x.newName : t }));
    }
  };
  return (
    <div>
      <div className="field"><label htmlFor="nj-truck">Truck *</label><TruckPicker id="nj-truck" value={truck} onChange={setTruck} /></div>
      <div className="ar-pair">
        <div className="field"><label htmlFor="nj-pri">Priority</label><PrioritySelect id="nj-pri" value={priority} onChange={setPriority} /></div>
        {!completed && <div className="field"><label htmlFor="nj-date">Date reported</label><input id="nj-date" type="date" value={opened} onChange={(e) => setOpened(e.target.value)} /></div>}
      </div>
      <div className="field">
        <label htmlFor="nj-title">Problem</label>
        <input id="nj-title" list="nj-cats" value={title} placeholder="e.g. Brakes" onChange={(e) => setTitleGuess(e.target.value)} />
        <datalist id="nj-cats">{CATEGORIES.map((x) => <option key={x} value={x} />)}</datalist>
      </div>
      <div className="field"><label htmlFor="nj-desc">{completed ? 'Issue / work summary' : 'Description'}</label><textarea id="nj-desc" rows={2} value={desc} placeholder="Describe the issue…" onChange={(e) => setDesc(e.target.value)} /></div>
      {!completed && drivers.length > 0 && (
        <div className="field"><label htmlFor="nj-by">Reported by</label>
          <select id="nj-by" value={reportedBy} onChange={(e) => setReportedBy(e.target.value)}>
            <option value="">—</option>
            {drivers.map((p) => <option key={p.user_id} value={p.user_id}>{p.display_name || p.email}</option>)}
          </select>
        </div>
      )}
      {completed && <CompletionFields v={d} set={(p) => setD({ ...d, ...p })} idp="lw" tasks={rules ?? []} />}
      {!completed && (
        <label className="row" style={{ gap: 10 }}>
          <input type="checkbox" checked={mech} onChange={(e) => setMech(e.target.checked)} style={{ width: 20, height: 20 }} />
          <span>Send to the mechanic</span>
        </label>
      )}
      <div className="row" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={!ok} onClick={save}>{completed ? 'Save record' : 'Create work order'}</button>
      </div>
    </div>
  );
}

/** Decline a driver's report (not a real problem, already fixed …) with a note the driver sees. */
export function DeclineForm({ onSave, onCancel }: { onSave: (note: string) => void; onCancel: () => void }) {
  const [note, setNote] = useState('');
  return (
    <div>
      <div className="field"><label htmlFor="dc-note">Note for the driver</label><textarea id="dc-note" rows={3} value={note} placeholder="e.g. Already fixed Tuesday — thanks!" onChange={(e) => setNote(e.target.value)} /></div>
      <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-danger" onClick={() => onSave(note)}>Decline report</button>
      </div>
    </div>
  );
}
